import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import api from '../../services/api.js'
import { useTranslation } from 'react-i18next'

/** Public, token-gated complaint form (no login required). */
export default function ComplaintForm() {
  const { t } = useTranslation('ops')
  const { token } = useParams()
  const [valid, setValid] = useState(null)   // null=checking, true/false
  const [checkError, setCheckError] = useState('')
  const [form, setForm] = useState({ name: '', organization: '', email: '', message: '' })
  const [files, setFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(null)     // complaint_number
  const [error, setError] = useState('')

  useEffect(() => {
    api.get(`/legal/complaint-invites/${token}/validate`)
      .then(() => setValid(true))
      .catch(e => { setValid(false); setCheckError(e.response?.data?.detail || 'This complaint link is invalid.') })
  }, [token])

  const submit = async (e) => {
    e.preventDefault()
    if (submitting) return
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) { setError('Name, email and complaint are required.'); return }
    if (files.length > 5) { setError('Please attach at most 5 files.'); return }
    setSubmitting(true); setError('')
    try {
      const fd = new FormData()
      fd.append('token', token)
      fd.append('name', form.name)
      fd.append('email', form.email)
      fd.append('message', form.message)
      if (form.organization) fd.append('organization', form.organization)
      files.forEach(f => fd.append('files', f))
      const r = await api.post('/legal/complaints', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setDone(r.data.complaint_number)
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not submit your complaint. The link may have expired.')
    } finally { setSubmitting(false) }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('register_complaint')}</h1>
          <p className="text-sm text-gray-500 mt-1">3 Pillars Data &amp; Training LLC</p>
        </div>

        {valid === null && <p className="text-center text-gray-500 py-8">{t('checking_link')}</p>}

        {valid === false && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700 text-center">{checkError}</div>
        )}

        {valid && done && (
          <div className="text-center py-6">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-gray-800 font-medium">{t('complaint_registered')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('reference_number')}</p>
            <p className="text-lg font-mono font-bold text-gray-900 mt-1">{done}</p>
            <p className="text-xs text-gray-400 mt-4">{t('team_notified')}</p>
          </div>
        )}

        {valid && !done && (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('full_name_req')}</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('organization')}</label>
              <input value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('email_req')}</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('complaint_req')}</label>
              <textarea rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('attachments_optional')}</label>
              <input
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.mp4"
                onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 5))}
                className="w-full text-xs text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <p className="text-[11px] text-gray-400 mt-1">Up to 5 files — screenshots, images, PDF, Word/Excel/PowerPoint, or a short MP4 (max 25 MB each).</p>
              {files.length > 0 && <p className="text-[11px] text-gray-500 mt-0.5">{files.length} file(s) selected</p>}
            </div>
            {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <button type="submit" disabled={submitting} className="w-full px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {submitting ? 'Submitting…' : 'Submit Complaint'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
